import { PocDivider } from 'commons/components/components-wrapper/poc-divider.component';
import type { PocInputValidity } from 'commons/types';
import { debounce } from 'commons/utils/functions.utils';
import { useTranslation } from 'core/i18n/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConnectivityProperties } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.model';
import { DeviceConfigStatusTrackingCard } from '../device-config-status-tracking-card/device-config-status-tracking-card.component';
import { StatusTrackingThresholdRow } from '../status-tracking-threshold-row/status-tracking-threshold-row.component';
import { STATUS_TRACKING_CHANGE_DEBOUNCE_MS, STATUS_TRACKING_TRANSLATION_PREFIX } from '../../constants';
import { MIN_CONNECTIVITY_HOURS } from './connectivity-status-tracking-card.constants';
import { useConnectivityStatusTrackingCardValidation } from './use-connectivity-status-tracking-card-validation.hook';

interface ConnectivityStatusTrackingCardProps {
    values: ConnectivityProperties | undefined;
    onValuesChange?: (values: ConnectivityProperties) => void;
    onValidityChange?: (isValid: PocInputValidity) => void;
}

export const ConnectivityStatusTrackingCard = ({
    values,
    onValuesChange,
    onValidityChange,
}: ConnectivityStatusTrackingCardProps) => {
    const { t } = useTranslation();
    const [connectivityStatus, setConnectivityStatus] = useState<ConnectivityProperties | undefined>(values);
    const connectivityStatusRef = useRef(connectivityStatus);
    connectivityStatusRef.current = connectivityStatus;

    const [previousValues, setPreviousValues] = useState(values);
    if (values !== previousValues) {
        setPreviousValues(values);
        setConnectivityStatus(values);
    }

    const { validity, getValidationMessageByType, checkValidation } = useConnectivityStatusTrackingCardValidation({
        onValidityChange,
    });

    const onChange = useCallback(
        (field: keyof ConnectivityProperties, value: number | boolean) => {
            const updatedStatus = {
                ...connectivityStatusRef.current,
                [field]: value,
            } as ConnectivityProperties;

            setConnectivityStatus(updatedStatus);

            const isValid = checkValidation(updatedStatus);

            if (isValid.state === 'valid') {
                onValuesChange?.(updatedStatus);
            }
        },
        [checkValidation, onValuesChange]
    );

    const handleChange = useMemo(() => debounce(onChange, STATUS_TRACKING_CHANGE_DEBOUNCE_MS), [onChange]);

    useEffect(
        () => () => {
            handleChange.cancel();
        },
        [handleChange]
    );

    return (
        <DeviceConfigStatusTrackingCard
            title={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.connectivity.title`)}
            description={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.connectivity.description`)}
            toggleChecked={connectivityStatus?.enabled}
            onToggleCheckedChange={(value) => handleChange('enabled', value)}
        >
            <StatusTrackingThresholdRow
                tagStatus="warning"
                label={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.connectivity.warning`)}
                value={connectivityStatus?.warningHours}
                minHours={MIN_CONNECTIVITY_HOURS}
                disabled={!connectivityStatus?.enabled}
                validity={validity}
                assistiveText={getValidationMessageByType()}
                inputTestId="connectivity-warning-hours-input"
                suffixTestId="connectivity-hours-suffix"
                assistiveTextTestId="connectivity-warning-hours-assistive-text"
                onChange={(value) => handleChange('warningHours', value)}
            />
            <PocDivider />
            <StatusTrackingThresholdRow
                tagStatus="alarm"
                label={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.connectivity.alert`)}
                value={connectivityStatus?.alertHours}
                minHours={MIN_CONNECTIVITY_HOURS}
                disabled={!connectivityStatus?.enabled}
                validity={validity}
                inputTestId="connectivity-alert-hours-input"
                suffixTestId="connectivity-hours-suffix"
                onChange={(value) => handleChange('alertHours', value)}
            />
        </DeviceConfigStatusTrackingCard>
    );
};
